var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
const getAudioResponse = z.object({
    microphoneMute: z.boolean(),
    microphonePickupState: z.string(),
    microphoneGain: z.number(),
    speakerTrebleLevel: z.number(),
    speakerBassLevel: z.number(),
    echoReductionLevel: z.string(),
    noiseReductionLevel: z.string(),
    auxiliaryOutputState: z.string(),
    voiceAmplificationEnabled: z.boolean(),
    voiceAmplificationLevel: z.number(),
    voiceAmplificationAuxInLevel: z.string(),
});
const allowedMuteTypes = ['input', 'reinforcement'];
const allowedVolumeTypes = ['output', 'reinforcement'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: ['preset'],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
const debug = false;
function isNurevaAudioChannelConfig(config) {
    return typeof config === 'string';
}
function isNurevaPresetMuteConfig(config) {
    return (typeof config === 'object' &&
        'mutePreset' in config &&
        typeof config.mutePreset === 'string' &&
        'muteStatus' in config &&
        typeof config.muteStatus === 'string' &&
        'unmutePreset' in config &&
        typeof config.unmutePreset === 'string');
}
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
class Channel {
    constructor(type, name, http, authToken, log) {
        this.type = type;
        this.name = name;
        this.http = http;
        this.authToken = authToken;
        this.log = log;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.key = `${type}.${name.toLowerCase()}`;
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    httpRequest(method, cmd, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = {
                Authorization: `Nureva ${this.authToken}`,
                'Content-Type': 'application/json',
                'Nureva-Client-Id': 'aveo',
                'Nureva-Client-Version': '1',
            };
            requestOptions.tags = tags;
            const resp = yield this.http.request(method, cmd.cmd, requestOptions);
            return resp;
        });
    }
    httpRequestJson(method, cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = {
                Authorization: `Nureva ${this.authToken}`,
                'Content-Type': 'application/json',
                'Nureva-Client-Id': 'aveo',
                'Nureva-Client-Version': '1',
            };
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson(method, cmd.cmd, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    matches(key) {
        return this.key === key;
    }
    setFader(value, currentValue, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
}
class Channels {
    constructor(http, log) {
        this.http = http;
        this.log = log;
        this.authToken = '';
        this.channels = [];
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    addChannel(type, name) {
        if (type === 'input') {
            this.channels.push(new InputChannel(name, this.http, this.authToken, this.log));
        }
        else if (type === 'output') {
            this.channels.push(new OutputChannel(name, this.http, this.authToken, this.log));
        }
        else if (type === 'preset') {
            throw new Error(`addChannel: preset channels not supported`);
        }
        else if (type === 'reinforcement') {
            this.channels.push(new ReinforcementChannel(name, this.http, this.authToken, this.log));
        }
    }
    clearChannels() {
        this.channels.length = 0;
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: type === 'preset',
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getAllConfiguredChannels() {
        return this.channels.filter(channel => channel.isMuteConfigured || channel.isVolumeConfigured);
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getChannelType(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel === null || channel === void 0 ? void 0 : channel.type;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                type: channel.type,
                name: channel.name,
                filters: this.computeFilters(channel.type),
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
}
class InputChannel extends Channel {
    constructor(name, http, authToken, log) {
        super('input', name, http, authToken, log);
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequestJson('get', { cmd: '/api/v1/audio' }, getAudioResponse, tags);
            return resp.microphoneMute;
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequest('patch', {
                cmd: '/api/v1/audio',
                data: {
                    microphoneMute: value,
                },
            }, tags);
            return (resp === null || resp === void 0 ? void 0 : resp.status) === 200 ? value : undefined;
        });
    }
}
class OutputChannel extends Channel {
    constructor(name, http, authToken, log) {
        super('output', name, http, authToken, log);
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined; // no volume query
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined; // no mute on outputs
        });
    }
    setFader(value, currentValue, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            let action = 'decrement';
            // Output volume is only an increment, but the volume function uses a range, so we map
            // volume values to increment or decrement.
            if (currentValue === 0 && value === 0) {
                action = 'decrement'; // continue decrementing at the bottom of the range
            }
            else if (value - currentValue >= 0) {
                action = 'increment';
            }
            const resp = yield this.httpRequest('put', {
                cmd: '/api/v1/audio/volume/change',
                data: { operation: action },
            }, tags);
            return (resp === null || resp === void 0 ? void 0 : resp.status) === 200 ? value : undefined;
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined; // no mute on outputs
        });
    }
}
class ReinforcementChannel extends Channel {
    constructor(name, http, authToken, log) {
        super('reinforcement', name, http, authToken, log);
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequestJson('get', { cmd: '/api/v1/audio' }, getAudioResponse, tags);
            return resp.voiceAmplificationLevel;
        });
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequestJson('get', { cmd: '/api/v1/audio' }, getAudioResponse, tags);
            return !resp.voiceAmplificationEnabled;
        });
    }
    setFader(value, currentValue, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequest('patch', {
                cmd: '/api/v1/audio',
                data: { voiceAmplificationLevel: value },
            }, tags);
            return (resp === null || resp === void 0 ? void 0 : resp.status) === 200 ? value : undefined;
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpRequest('patch', {
                cmd: '/api/v1/audio',
                data: { voiceAmplificationEnabled: !value },
            }, tags);
            return (resp === null || resp === void 0 ? void 0 : resp.status) === 200 ? value : undefined;
        });
    }
}
export class Nureva {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.authToken = '';
        this.configuredChannels = [];
        this.channelPollIndex = 0;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const detectedChannels = this.channels.getDetected();
                if (debug) {
                    this.log.debug(`detected channels = ${JSON.stringify(detectedChannels, null, 4)}`);
                }
                return detectedChannels;
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error('no channel found for ${key}');
                return (_a = (yield channel.getMute(tags))) !== null && _a !== void 0 ? _a : this.state.mutes.values[key];
            }),
            set: (key, mute, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error('no channel found for ${key}');
                return (_b = (yield channel.setMute(mute, tags))) !== null && _b !== void 0 ? _b : this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // Only increment/decrement supported in Nureva. Volume query is not supported.
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error('no channel found for ${key}');
                return (_c = (yield channel.getFader(tags))) !== null && _c !== void 0 ? _c : this.state.volumes.values[key];
            }),
            set: (key, volume, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error('no channel found for ${key}');
                return ((_d = (yield channel.setFader(volume, this.state.volumes.values[key], tags))) !== null && _d !== void 0 ? _d : this.state.volumes.values[key]);
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const isVoiceAmplification = this.channels.getChannelType(config) === 'reinforcement';
                if (debug) {
                    this.log.debug(`range: config = ${JSON.stringify(config)}, info = ${JSON.stringify(info)}  `);
                }
                if (isVoiceAmplification) {
                    if (info.volumeType === 'trim')
                        return { max: 40, min: 16, round: 1 };
                    return { max: 40, min: 0, round: 1 };
                }
                // Main volume is only increment / decrement so the range not used.
                if (info.volumeType === 'trim')
                    return { max: 100, min: 0, round: 1 };
                return { max: 100, min: 0, round: 1 };
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.configuredChannels.length > 0) {
                        const channel = this.configuredChannels[this.channelPollIndex];
                        if (channel !== undefined) {
                            const mute = yield channel.getMute(['poll']);
                            if (channel.isMuteConfigured)
                                this.state.mutes.update(channel.key, mute);
                        }
                        this.channelPollIndex++;
                        if (this.channelPollIndex >= this.configuredChannels.length) {
                            this.channelPollIndex = 0;
                        }
                    }
                    else {
                        // If no channels configured, send a dummy poll to prevent an idle timeout.
                        yield this.httpRequest('get', { cmd: '/api/v1/audio' }, ['poll']);
                    }
                }),
            },
        ];
        this.channels = new Channels(http, log);
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.channels.clearChannels();
            this.channels.addChannel('input', 'Microphone');
            this.channels.addChannel('reinforcement', 'Voice Amplification');
            this.channels.addChannel('output', 'Loudspeaker');
        });
    }
    onInitialize(credentials) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'admin';
            const requestOptions = {
                data: { account: username, password: password },
                tags: ['initialize'],
                headers: {
                    'Nureva-Client-Id': 'aveo',
                    'Nureva-Client-Version': '1',
                },
            };
            const resp = yield this.http.request('post', '/api/v1/auth/login', requestOptions);
            if ((resp === null || resp === void 0 ? void 0 : resp.status) === 200) {
                let data = {};
                try {
                    data = JSON.parse((_c = resp === null || resp === void 0 ? void 0 : resp.data) !== null && _c !== void 0 ? _c : '{}');
                }
                catch (error) {
                    this.log.error(`failed parsing JSON: ${resp === null || resp === void 0 ? void 0 : resp.data}`);
                    return `verifyFailed`;
                }
                const loginResp = z.object({ authParameters: z.string() });
                if (!isResp(loginResp, data)) {
                    this.log.error(`unexpected response: ${JSON.stringify(data)}`);
                    return `verifyFailed`;
                }
                this.authToken = data.authParameters;
                this.channels.authToken = this.authToken;
                return 'connected';
            }
            else if ((resp === null || resp === void 0 ? void 0 : resp.status) === 401) {
                return 'authFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    getKey(config) {
        if (isNurevaPresetMuteConfig(config))
            return config.muteStatus;
        if (isNurevaAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    // Returns the full response to allow testing status value. As there are no responses with
    // 'data', we do not use the Zod schema.
    httpRequest(method, cmd, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = {
                Authorization: `Nureva ${this.authToken}`,
                'Content-Type': 'application/json',
                'Nureva-Client-Id': 'aveo',
                'Nureva-Client-Version': '1',
            };
            requestOptions.tags = tags;
            return yield this.http.request(method, cmd.cmd, requestOptions);
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (debug) {
                this.log.debug(`inside validate for key: ${key}, config: ${JSON.stringify(config)}, info: ${JSON.stringify(info)}`);
            }
            if (isNurevaPresetMuteConfig(config)) {
                const muteStatusChannel = this.channels.getByKey(config.muteStatus);
                const mutePresetChannel = this.channels.getByKey(config.mutePreset);
                const unmutePresetChannel = this.channels.getByKey(config.unmutePreset);
                if (muteStatusChannel === undefined ||
                    mutePresetChannel === undefined ||
                    unmutePresetChannel === undefined) {
                    return 'missing';
                }
                if (muteStatusChannel.type === 'preset' ||
                    mutePresetChannel.type !== 'preset' ||
                    unmutePresetChannel.type !== 'preset') {
                    return 'incompatible';
                }
                // Configure the mute and unmute presets.
                if (muteStatusChannel) {
                    muteStatusChannel.isMuteConfigured = true;
                    muteStatusChannel.presetMute = {
                        mute: mutePresetChannel,
                        unmute: unmutePresetChannel,
                    };
                }
                return 'ok';
            }
            else if (isNurevaAudioChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (channel.type === 'preset')
                    return 'incompatible';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    // Check if volume mute can be supported by the channel.
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                if (debug)
                    this.log.debug(`validChannel: ${JSON.stringify(config, null, 4)}`);
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
Nureva.channelsConfigName = 'nurevaControlChannels';
Nureva.driverType = 'httpDriver';
Nureva.key = '-O46Ipf57IeDYjwFfKWV';
Nureva.httpRequestOptions = {
    scheme: 'https',
    idleTimeoutMs: 20000,
};
